import { cn } from '../../lib/utils'
import { DetailSkeleton } from '../finance/SkeletonStates'
import { AlertCircle, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

interface AttentionItem {
  severity: 'critical' | 'high' | 'medium' | 'low'
  title: string
  detail: string
  link?: string
}

interface MorningBriefingProps {
  narrative?: string
  attentionItems?: AttentionItem[]
  timestamp?: string
  loading?: boolean
  className?: string
}

const severityDot: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-amber-500',
  medium: 'bg-cyan-500',
  low: 'bg-slate-500',
}

export function MorningBriefing({
  narrative, attentionItems = [], timestamp, loading, className,
}: MorningBriefingProps) {
  const navigate = useNavigate()

  if (loading) return <DetailSkeleton className={className} />

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
        'border-l-[3px] border-l-cyan-500 p-5',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
          Morning Briefing
        </span>
        {timestamp && (
          <span className="text-[10px] text-[var(--text-hint,#475569)]">
            {timestamp}
          </span>
        )}
      </div>

      {/* Narrative */}
      {narrative ? (
        <p className="text-[13px] leading-relaxed text-[var(--text-secondary,#94A3B8)] mb-4">
          {narrative}
        </p>
      ) : (
        <p className="text-[13px] text-[var(--text-muted,#64748B)] italic mb-4">
          No briefing available for this period.
        </p>
      )}

      {/* Attention items */}
      {attentionItems.length > 0 && (
        <div className="space-y-2">
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Requires Attention
          </span>
          {attentionItems.slice(0, 5).map((item, i) => (
            <div
              key={i}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2',
                'bg-white/[0.02] hover:bg-white/[0.04] transition-colors',
                item.link && 'cursor-pointer',
              )}
              onClick={item.link ? () => navigate(item.link!) : undefined}
            >
              <div className={cn('h-2 w-2 rounded-full shrink-0', severityDot[item.severity])} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[var(--text-primary,#F0F2F5)] truncate">
                  {item.title}
                </div>
                <div className="text-[11px] text-[var(--text-muted,#64748B)] truncate">
                  {item.detail}
                </div>
              </div>
              {item.link && (
                <ChevronRight className="h-3.5 w-3.5 text-[var(--text-hint,#475569)] shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  )
}
